import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';

import {
  ThemeContext,
  readStoredPreference,
  storePreference,
  systemPrefersDark,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';

/*
 * Applies the resolved theme as classes on <html>: `.dark` forces the dark
 * token set, `.light` opts out of the prefers-color-scheme dark block (see
 * tokens.css). index.html carries a tiny bootstrap script that sets the same
 * classes before first paint to avoid a flash.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.classList.toggle('light', resolved === 'light');

    // Keep browser chrome (meta theme-color) in step with the *effective* theme,
    // not just prefers-color-scheme — a manual override must move the chrome too.
    // The colour is read from the `--bg` token so the hex lives only in tokens.css.
    const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
    if (!bg) return;
    document
      .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
      .forEach((meta) => meta.setAttribute('content', bg));
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    storePreference(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
